/**
 * "For your quest" — the pure half of the Crafting panel's lead card (q-demo-crafting-clarity).
 *
 * Owner, Desk #279 (2026-10-27), from an 8-year-old's phone playtest: the crafting menu itself
 * "wasn't clear". The cause: the panel opened on an explanation of the four-roll resolver, then a
 * list of whatever the bag could pay for (nothing, if one input was short), then a manual form
 * asking for a recipe id. Nothing said which recipe the quest wanted, or what was still missing.
 *
 * This picks the recipe the tracked quest asks for and states each input as have / need, so the
 * card can show one Craft button, enabled exactly when the bag can pay. It decides nothing the
 * server does not re-check: the craftIntent it builds is `payFor`'s, the same the list sends.
 */

import {
  payFor,
  type CraftInputChoice,
  type CraftRecipeView,
  type HeldStack,
} from './craftable.js';

/** The quest-log fields read here (QuestLogWire's quests). */
export interface QuestCraftLike {
  questId: string;
  name: string;
  status: string;
  objectives: readonly {
    verb: string;
    target: string | null;
    count: number;
    progress: number;
    done: boolean;
  }[];
}

/** One recipe input as the card shows it. */
export interface InputNeed {
  resource: string;
  /** Units held that this input accepts (at or above its minimum quality). */
  have: number;
  need: number;
  enough: boolean;
}

export interface QuestCraftCard {
  questId: string;
  questName: string;
  recipe: CraftRecipeView;
  needs: InputNeed[];
  /** The stacks a Craft press sends, or null while the bag cannot pay. */
  inputs: CraftInputChoice[] | null;
}

/**
 * The recipe a craft objective names: the objective's target is a recipe id or the item a
 * recipe makes (types.ts `QuestObjective`). Retired recipes are never offered.
 */
export function recipeForCraftTarget(
  recipes: readonly CraftRecipeView[],
  target: string | null | undefined,
): CraftRecipeView | undefined {
  if (!target) return undefined;
  const live = recipes.filter((r) => r.retired !== true);
  return live.find((r) => r.id === target) ?? live.find((r) => r.output === target);
}

/** Each input of `recipe` as have / need against the bag. */
export function recipeNeeds(recipe: CraftRecipeView, held: readonly HeldStack[]): InputNeed[] {
  const out: InputNeed[] = [];
  for (const input of recipe.inputs ?? []) {
    if (input.resource === undefined) continue;
    const have = held
      .filter((s) => s.contentId === input.resource && s.quality >= (input.minQuality ?? 1))
      .reduce((sum, s) => sum + s.quantity, 0);
    out.push({
      resource: input.resource,
      have,
      need: input.quantity,
      enough: have >= input.quantity,
    });
  }
  return out;
}

/**
 * The card for the first quest in log order (the order the HUD tracks) that is active with an
 * unfinished craft objective whose recipe the pack ships; null when no quest asks for a craft.
 */
export function questCraftCard(
  quests: readonly QuestCraftLike[],
  recipes: readonly CraftRecipeView[],
  held: readonly HeldStack[],
): QuestCraftCard | null {
  for (const quest of quests) {
    if (quest.status !== 'active') continue;
    for (const o of quest.objectives) {
      if (o.verb !== 'craft' || o.done) continue;
      const recipe = recipeForCraftTarget(recipes, o.target);
      if (!recipe) continue;
      return {
        questId: quest.questId,
        questName: quest.name,
        recipe,
        needs: recipeNeeds(recipe, held),
        inputs: payFor(recipe, held),
      };
    }
  }
  return null;
}
